/* eslint-disable sonarjs/cognitive-complexity */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ColorChipButton,
  ColorTextWrapper,
  NextButton,
  ColorChipText,
} from './styles/ColorSwatches.style';
import { getIconPath, getViewportInfo } from '../../../../../utils';
import { HLP_COLOR_SWATCH_SCROLL_CONFIG } from '../plpTileConstants';
import BodyCopy from '../../../../common/atoms/BodyCopy';

let scrollConfig = { ...HLP_COLOR_SWATCH_SCROLL_CONFIG.desktop };

export const useIntersectionObserver = (options = {}, defaultVisibility = false) => {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(defaultVisibility);

  const callbackFn = (entries) => {
    const [entry] = entries;
    setIsVisible(entry.isVisible);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(callbackFn, options);
    if (containerRef?.current) observer.observe(containerRef?.current);
    return () => {
      if (containerRef?.current) observer.unobserve(containerRef?.current);
    };
  }, [containerRef?.current?.getBoundingClientRect(), options]);

  return [containerRef, isVisible];
};

const ColorSwatchesHlp = ({
  onQuickViewOpenClick,
  saveDataForQv,
  isBundleProduct,
  handleViewBundleClick,
  colorSwatchList,
  isTileRedesignAB,
}) => {
  const [shouldScroll, setShouldScroll] = useState(false);
  const arrowRight = getIconPath('icon-carrot-black-small');
  const productColorListRef = useRef(null);

  const [scrollEndlRef, isEndTileInView] = useIntersectionObserver({
    root: productColorListRef?.current,
    rootMargin: '0px',
    threshold: 0,
    trackVisibility: true,
    delay: 100,
  });

  useEffect(() => {
    const { isTablet, isMobile } = getViewportInfo();
    if (isTablet) {
      scrollConfig = { ...HLP_COLOR_SWATCH_SCROLL_CONFIG.tablet };
      if (
        colorSwatchList.length * scrollConfig.scrollWidth >
        productColorListRef?.current?.clientWidth
      ) {
        setShouldScroll(true);
      }
    } else if (isMobile) {
      scrollConfig = { ...HLP_COLOR_SWATCH_SCROLL_CONFIG.mobile };
      if (
        colorSwatchList.length * scrollConfig.scrollWidth >
        productColorListRef?.current?.clientWidth
      ) {
        setShouldScroll(true);
      }
    } else {
      scrollConfig = { ...HLP_COLOR_SWATCH_SCROLL_CONFIG.desktop };

      if (
        colorSwatchList.length * scrollConfig.scrollWidth >
        productColorListRef?.current?.clientWidth
      ) {
        setShouldScroll(true);
      }
    }
  }, [colorSwatchList]);

  const clickHandler = (swatchId) => {
    requestAnimationFrame(() => {
      if (isBundleProduct) {
        handleViewBundleClick();
      }
      saveDataForQv();
      onQuickViewOpenClick({
        colorProductId: swatchId,
        fromPage: 'PLP',
        fromPageBranch: 'HPLP',
      });
    });
  };

  const scrollToNextColorSwatch = () => {
    if (productColorListRef) {
      productColorListRef.current.scrollLeft += scrollConfig.scrollWidth;
      if (isEndTileInView) {
        productColorListRef.current.scrollLeft = 0;
      }
    }
  };

  if (isTileRedesignAB === 'variationB') {
    return (
      <ColorTextWrapper>
        <BodyCopy className="color-text" fontFamily="secondary" fontSize="fs12">
          {`${colorSwatchList?.length || 1} Colors`}
        </BodyCopy>
      </ColorTextWrapper>
    );
  }
  if (!isTileRedesignAB && colorSwatchList?.length <= 1) {
    return <ColorTextWrapper isEmpty>{''}</ColorTextWrapper>;
  }
  let list = colorSwatchList;
  if (isTileRedesignAB && isTileRedesignAB === 'variationA')
    list = colorSwatchList.filter((val, idx) => idx < 4);

  return (
    <div
      className={`flex-row justify-between color-swatch-container-hlp${
        isTileRedesignAB ? ' tile-redesign' : ''
      }`}
    >
      <>
        <ol className="flex-row color-swatch-list" ref={productColorListRef}>
          {list?.map((colorSwatch) => {
            return (
              <ColorChipButton
                key={colorSwatch.id}
                type="button"
                title={colorSwatch.name}
                isActive={colorSwatch.isActive}
                name={colorSwatch.name}
                imageUrl={colorSwatch.imageUrl}
                onClick={() => clickHandler(colorSwatch.id)}
                isHlp
              />
            );
          })}
          {isTileRedesignAB && isTileRedesignAB === 'variationA' && colorSwatchList.length > 4 && (
            <ColorChipText onClick={() => clickHandler(colorSwatchList[0]?.id)}>
              <BodyCopy
                className="color-text"
                fontFamily="secondary"
                fontSize="fs12"
                component="div"
              >
                {`+${colorSwatchList?.length - 4}`}
              </BodyCopy>
            </ColorChipText>
          )}
          <div ref={scrollEndlRef} key={123} className="swatch-end" />
        </ol>
        {!isTileRedesignAB && (
          <NextButton
            onClick={scrollToNextColorSwatch}
            type="button"
            arrowRight={arrowRight}
            data-locator="color_swatch_arrow"
            shouldScroll={shouldScroll}
          />
        )}
      </>
    </div>
  );
};
ColorSwatchesHlp.propTypes = {
  onQuickViewOpenClick: PropTypes.func,
  saveDataForQv: PropTypes.func,
  productPartNo: PropTypes.string,
  TCPSwatches: PropTypes.string,
  assetHost: PropTypes.string.isRequired,
  assetPath: PropTypes.string.isRequired,
  swatchImg: PropTypes.string,
  tcpColor: PropTypes.string,
  relatedSwatchImages: PropTypes.arrayOf(PropTypes.shape({})),
};
ColorSwatchesHlp.defaultProps = {
  onQuickViewOpenClick: () => {},
  saveDataForQv: () => {},
  productPartNo: '',
  TCPSwatches: '',
  swatchImg: '',
  tcpColor: '',
  relatedSwatchImages: [],
};
export default ColorSwatchesHlp;
